import React from 'react';
import { useTranslation } from 'react-i18next';
import type { StructuredResultItem } from '../types';
import { formatDistance } from '../utils';

interface AttractionResultItemRendererProps {
  item: StructuredResultItem;
}

export const AttractionResultItemRenderer: React.FC<AttractionResultItemRendererProps> = ({ item }) => {
  const { t } = useTranslation();

  const getAttractionDetails = () => {
    const details = [];
    
    if (item.opening_hours) details.push({
      content: `🕐 ${item.opening_hours}`,
      testId: 'opening-hours'
    });
    
    // Common location info
    details.push({
      content: `📍 ${item.location}`,
      testId: 'location'
    });
    if (item.distance_from_user !== undefined && item.distance_from_user !== null) {
      details.push({
        content: `📏 ${formatDistance(item.distance_from_user)} km ${t('results.distance.away')}`,
        testId: 'distance-from-user'
      });
    }
    
    return details;
  };

  const getAttractionBadge = () => {
    if (!item.attraction_type) return null;
    
    const attractionIcons = {
      museum: '🏛️',
      historical_site: '🏰',
      cave: '🕳️',
      viewpoint: '🏔️',
      waterfall: '💧',
      park: '🌳',
      lake: '🏞️'
    };
    
    const icon = attractionIcons[item.attraction_type as keyof typeof attractionIcons] || '🏛️';
    
    return { text: item.attraction_type, icon };
  };

  const badge = getAttractionBadge();

  return (
    <article className="border rounded p-3 bg-blue-50 text-sm border-blue-100" data-testid="attraction-item">
      {/* Title and Badge */}
      <div className="flex items-start justify-between mb-2">
        <h5 className="font-semibold text-gray-900 leading-tight flex-1 pr-2" data-testid="item-title">
          {item.title}
        </h5>
        {badge && (
          <span 
            className="px-2 py-0.5 rounded text-xs font-medium shrink-0 bg-blue-100 text-blue-800 border border-blue-200"
            data-testid="attraction-badge"
            role="status"
            aria-label={`Attraction type: ${badge.text}`}
          >
            {badge.icon} {badge.text}
          </span>
        )}
      </div>
      
      {/* Attraction-specific details */}
      <div className="flex flex-wrap gap-4 mb-2 text-xs text-gray-600" data-testid="attraction-details">
        {getAttractionDetails().map((detail, index) => (
          <span key={index} data-testid={detail.testId}>
            {detail.content}
          </span>
        ))}
      </div>
      
      {/* Description */}
      <p className="text-gray-700 mb-2 text-sm leading-tight" data-testid="item-description">
        {item.description}
      </p>
      
      {/* Link */}
      <a 
        href={item.url} 
        target="_blank" 
        rel="noopener noreferrer"
        className="inline-flex items-center text-blue-600 hover:text-blue-800 text-xs font-medium"
        data-testid="visit-website-link"
        aria-label={`Visit website for ${item.title}`}
      >
        🔗 {t('results.actions.visitWebsite')}
      </a>
    </article>
  );
};